(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-aofp-section]';
  const sectionControllers = new WeakMap();
  const stickyStates = new WeakMap();
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const allowsMotion = (root) => root.dataset.aofpMotion !== 'false' && !reducedMotion();
  // Theme Editor preview: show animated content in its finished state (the storefront keeps its motion).
  const editorPreview = () => Boolean(window.Shopify && window.Shopify.designMode);
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const pageScope = (root) => root.closest('main') || document;

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope?.matches?.(SECTION_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(SECTION_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const parseMoneyFormat = (raw) => {
    let value = raw || '{{amount}}';
    for (let pass = 0; pass < 2 && typeof value === 'string'; pass += 1) {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed !== 'string') break;
        value = parsed;
      } catch (_error) {
        break;
      }
    }
    return String(value || '{{amount}}').replaceAll('&quot;', '"');
  };

  const formatMoney = (cents, rawFormat) => {
    const value = Number(cents || 0);
    const format = parseMoneyFormat(rawFormat);
    const token = (format.match(/{{\s*(\w+)\s*}}/) || [null, 'amount'])[1];
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (Number(number) / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    const replacements = {
      amount: withDelimiters(value),
      amount_no_decimals: withDelimiters(value, 0),
      amount_with_comma_separator: withDelimiters(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelimiters(value, 0, '.', ','),
      amount_with_apostrophe_separator: withDelimiters(value, 2, "'", '.'),
      amount_no_decimals_with_space_separator: withDelimiters(value, 0, ' ', '.'),
      amount_with_space_separator: withDelimiters(value, 2, ' ', ','),
      amount_with_period_and_space_separator: withDelimiters(value, 2, ' ', '.')
    };
    const rendered = format
      .replace(/{{\s*\w+\s*}}/, replacements[token] ?? replacements.amount)
      .replace(/([.,])00(?=\D*$)/, '');
    const decoder = document.createElement('textarea');
    decoder.innerHTML = rendered;
    return decoder.value;
  };

  const pauseMedia = (slide) => {
    slide?.querySelectorAll('video').forEach((video) => video.pause());
    slide?.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
  };

  const initGallery = (root, signal) => {
    const gallery = root.querySelector('[data-aofp-gallery]');
    if (!gallery) return;

    const slides = [...gallery.querySelectorAll('[data-aofp-gallery-slide]')];
    const buttons = [...gallery.querySelectorAll('[data-aofp-gallery-button]')];
    // v4 position dots (HOME-SPEC-V4 §3.7): not thumbnails and not a tablist — aria-current + roving tabindex only.
    const dots = [...gallery.querySelectorAll('[data-aofp-gallery-dot]')];
    const stage = gallery.querySelector('.aofp-buy__stage');
    const thumbRail = gallery.querySelector('.aofp-buy__thumbs');
    const indexNode = gallery.querySelector('[data-aofp-gallery-index]');
    const previous = gallery.querySelector('[data-aofp-gallery-prev]');
    const next = gallery.querySelector('[data-aofp-gallery-next]');
    if (!slides.length) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true' && !slide.hidden));
    const primaryKey = (slides.find((slide) => slide.hasAttribute('data-aofp-primary')) || slides[activeIndex]).dataset.aofpGalleryMedia;
    let swipeStart = null;

    const activate = (requested, options = {}) => {
      const { announce = true, focusButton = false, focusDot = false, revealButton = true } = options;
      let nextIndex = slides.findIndex((slide) => String(slide.dataset.aofpGalleryMedia) === String(requested));
      if (nextIndex < 0 && typeof requested === 'number' && Number.isInteger(requested)) nextIndex = requested;
      if (nextIndex < 0 || nextIndex >= slides.length) return false;

      activeIndex = nextIndex;
      const activeKey = String(slides[activeIndex].dataset.aofpGalleryMedia);
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) pauseMedia(slide);
      });
      buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === activeIndex
          || String(button.dataset.aofpGalleryMedia) === activeKey;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.removeAttribute('aria-pressed');
        button.tabIndex = active ? 0 : -1;
      });
      let activeDot = null;
      dots.forEach((dot) => {
        const active = String(dot.dataset.aofpGalleryMedia) === activeKey;
        dot.classList.toggle('is-active', active);
        if (active) {
          dot.setAttribute('aria-current', 'true');
          activeDot = dot;
        } else {
          dot.removeAttribute('aria-current');
        }
        dot.tabIndex = active ? 0 : -1;
      });
      if (!activeDot && dots.length) dots[0].tabIndex = 0;
      if (focusDot) activeDot?.focus({ preventScroll: true });
      const activeButton = buttons.find((button) => button.getAttribute('aria-selected') === 'true');
      if (revealButton && activeButton && thumbRail) {
        const railBounds = thumbRail.getBoundingClientRect();
        const buttonBounds = activeButton.getBoundingClientRect();
        const delta = buttonBounds.left < railBounds.left
          ? buttonBounds.left - railBounds.left
          : buttonBounds.right > railBounds.right
            ? buttonBounds.right - railBounds.right
            : 0;
        if (delta) thumbRail.scrollBy({ left: delta, behavior: allowsMotion(root) ? 'smooth' : 'auto' });
      }
      if (focusButton) activeButton?.focus({ preventScroll: true });
      if (indexNode) {
        // Visually hidden live status (the visible "01 / 07" capsule is gone); slide alts are not announced.
        const status = document.documentElement.lang.split('-')[0].toLowerCase() === 'ar' ? `الصورة ${activeIndex + 1} من ${slides.length}` : `Image ${activeIndex + 1} of ${slides.length}`;
        if (indexNode.textContent.trim() !== status) indexNode.textContent = status;
        indexNode.removeAttribute('aria-label');
      }
      return true;
    };

    const move = (direction, options = {}) => {
      if (slides.length < 2) return;
      activate((activeIndex + direction + slides.length) % slides.length, options);
    };

    const handleKey = (event, focus = {}) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') activate(0, focus);
      else if (event.key === 'End') activate(slides.length - 1, focus);
      else move(event.key === 'ArrowRight' ? 1 : -1, focus);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => activate(button.dataset.aofpGalleryMedia), { signal });
      button.addEventListener('keydown', (event) => handleKey(event, { focusButton: true }), { signal });
    });
    previous?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });
    stage?.addEventListener('keydown', (event) => handleKey(event), { signal });
    if (dots.length) {
      // Delegated: one click + one keydown listener for every dot in this gallery (removed on section unload).
      gallery.addEventListener('click', (event) => {
        const dot = event.target.closest?.('[data-aofp-gallery-dot]');
        if (dot && gallery.contains(dot)) activate(dot.dataset.aofpGalleryMedia);
      }, { signal });
      gallery.addEventListener('keydown', (event) => {
        if (event.target.closest?.('[data-aofp-gallery-dot]')) handleKey(event, { focusDot: true });
      }, { signal });
    }

    const releasePointer = (pointerId) => {
      if (stage?.hasPointerCapture?.(pointerId)) stage.releasePointerCapture(pointerId);
    };
    stage?.addEventListener('pointerdown', (event) => {
      if (event.target.closest('video, iframe, model-viewer, button, a, input, select, textarea')) return;
      swipeStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
      try {
        stage.setPointerCapture?.(event.pointerId);
      } catch (_error) {
        // Synthetic or already released pointers cannot be captured; the swipe still resolves on pointerup.
      }
    }, { signal });
    stage?.addEventListener('pointerup', (event) => {
      if (!swipeStart || event.pointerId !== swipeStart.pointerId) return;
      const deltaX = event.clientX - swipeStart.x;
      const deltaY = event.clientY - swipeStart.y;
      releasePointer(event.pointerId);
      swipeStart = null;
      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      move(deltaX < 0 ? 1 : -1);
    }, { signal });
    stage?.addEventListener('pointercancel', (event) => {
      releasePointer(event.pointerId);
      swipeStart = null;
    }, { signal });

    root.aofpGallerySelectMedia = (mediaId) => activate(mediaId);
    // Theme Editor: block:deselect on a community frame returns the gallery to the primary product slide (parity with Oil).
    root.aofpGalleryRestore = () => activate(primaryKey, { announce: false });
    signal.addEventListener('abort', () => {
      delete root.aofpGallerySelectMedia;
      delete root.aofpGalleryRestore;
    }, { once: true });
    activate(activeIndex, { announce: false, revealButton: false });
  };

  const fetchCart = async (timeout = 1800) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch((window.routes?.cart_url || '/cart') + '.js', {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_error) {
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const syncCartCount = (count) => {
    const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    if (window.AofSharedChrome?.setCartCount) {
      window.AofSharedChrome.setCartCount(safeCount);
      return;
    }
    document.querySelectorAll('[data-aof-cart-count], [data-aocdx-cart-count], [data-aofp-cart-count]').forEach((node) => {
      node.textContent = String(safeCount);
    });
    document.querySelectorAll('[data-aof-cart-label], [data-aocdx-cart-label], [data-aofp-cart-label]').forEach((node) => {
      node.removeAttribute('aria-label');
    });
  };

  const openCart = (trigger) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(trigger);
      return true;
    }
    const cartLink = document.querySelector('#cart-icon-bubble, [data-aof-cart-label], a[href$="/cart"]');
    if (cartLink && typeof cartLink.click === 'function') {
      cartLink.click();
      return true;
    }
    return false;
  };

  const variantQuantity = (cart, variantId) => (cart?.items || [])
    .filter((item) => String(item.variant_id) === String(variantId))
    .reduce((total, item) => total + Number(item.quantity || 0), 0);

  const readDrawerCart = () => {
    const items = [...document.querySelectorAll('cart-drawer .cart-item')].map((item) => {
      const key = String(item.dataset.lineKey || item.dataset.key || '');
      const variantId = key.split(':')[0] || item.dataset.variantId;
      const quantityNode = item.querySelector('[data-aoc-quantity-value], input[name="updates[]"], .quantity__input');
      const quantity = Number(item.dataset.quantity || quantityNode?.value || quantityNode?.textContent || 0);
      return { variant_id: Number(variantId), quantity };
    }).filter((item) => item.variant_id && item.quantity > 0);
    if (!items.length) return null;
    return {
      item_count: items.reduce((total, item) => total + item.quantity, 0),
      items
    };
  };

  const initCommerce = (root, signal) => {
    if (!root.matches('[data-aofp-buy]')) return;

    const scope = pageScope(root);
    const plans = [...root.querySelectorAll('[data-aofp-course-option]')]
      .sort((first, second) => Number(first.dataset.aofpCourseOption) - Number(second.dataset.aofpCourseOption));
    const quantityInput = root.querySelector('[data-aofp-quantity]');
    const quantityMinus = root.querySelector('[data-aofp-quantity-minus]');
    const quantityPlus = root.querySelector('[data-aofp-quantity-plus]');
    const status = root.querySelector('[data-aofp-status]');
    const nativeRoot = root.querySelector('.aofp-native-commerce');
    const nativeBreaks = nativeRoot?.querySelector('quantity-breaks');
    const nativeSubmit = nativeRoot?.querySelector('[id^="ProductSubmitButton-"], button[name="add"][type="submit"]');
    const nativeForm = nativeSubmit?.closest('form');
    const nativeVariant = nativeForm?.querySelector('input[name="id"]');
    const nativeRadios = [1, 2, 3].map((plan) => nativeRoot?.querySelector('input[name="quantity"][data-input="input_' + plan + '"]'));
    const nativeOffers = [...(nativeRoot?.querySelectorAll('quantity-break-upsells[data-offer]') || [])];
    const protocolVariantId = String(root.dataset.aofpProtocolVariant || '').trim();
    const oilVariantId = String(root.dataset.aofpOilVariant || '').trim();
    const protocolAvailable = root.dataset.aofpProtocolAvailable === 'true';
    const oilAvailable = root.dataset.aofpOilAvailable === 'true';
    const moneyFormat = root.dataset.aofpMoneyFormat || '{{amount}}';
    const validVariantId = (value) => /^\d+$/.test(String(value)) && String(value) !== '0';
    const expectedPlans = {
      1: { protocol: 1, oil: 0 },
      2: { protocol: 2, oil: 0 },
      3: { protocol: 1, oil: 1 }
    };
    const copy = {
      product: root.dataset.aofpStatusProduct || 'This product is currently unavailable.',
      oil: root.dataset.aofpStatusOil || 'The Original Afghani Hair Oil is currently unavailable. The Protocol courses remain available.',
      config: root.dataset.aofpStatusConfig || 'Purchasing is paused because the Shopify cart configuration could not be verified.',
      adding: root.dataset.aofpStatusAdding || 'Adding your selected course to the cart…',
      cartError: root.dataset.aofpStatusCartError || 'The cart did not update. Please try again.',
      cartReview: root.dataset.aofpStatusCartReview || 'We could not confirm the cart update. Opening your cart for review.'
    };

    nativeRoot?.querySelectorAll('button, input, select, textarea, a').forEach((node) => {
      node.tabIndex = -1;
    });
    nativeRadios.forEach((radio) => {
      if (radio && !radio.dataset.aofpBaseQuantity) radio.dataset.aofpBaseQuantity = radio.value;
    });

    const visiblePlanIds = plans.map((plan) => Number(plan.dataset.aofpCourseOption));
    const visibleMappingValid = plans.length === 3
      && visiblePlanIds.join('/') === '1/2/3'
      && plans.every((plan) => {
        const planId = Number(plan.dataset.aofpCourseOption);
        const expected = expectedPlans[planId];
        return expected
          && Number(plan.dataset.protocolQuantity) === expected.protocol
          && Number(plan.dataset.oilQuantity) === expected.oil
          && Number(plan.dataset.price) > 0;
      });
    const nativeMappingValid = nativeRadios.every(Boolean)
      && nativeRadios.every((radio, index) => Number(radio.dataset.aofpBaseQuantity) === expectedPlans[index + 1].protocol);
    const baseBridgeValid = Boolean(
      nativeRoot
      && nativeBreaks
      && nativeSubmit
      && nativeForm
      && nativeVariant
      && validVariantId(protocolVariantId)
      && String(nativeVariant.value) === protocolVariantId
      && visibleMappingValid
      && nativeMappingValid
    );
    const systemOffer = nativeOffers.find((offer) => Number(offer.dataset.offer) === 3);
    const systemOilItem = [...(systemOffer?.querySelectorAll('.quantity-break__upsell[data-product]') || [])]
      .find((item) => String(item.dataset.product) === oilVariantId && item.dataset.type === 'gift');
    const systemBridgeValid = Boolean(
      validVariantId(oilVariantId)
      && systemOffer
      && systemOilItem
    );

    let currentPlan = Number(plans.find((plan) => plan.getAttribute('aria-checked') === 'true')?.dataset.aofpCourseOption || 1);
    if (!expectedPlans[currentPlan]) currentPlan = 1;
    let busy = false;

    const getQuantity = () => {
      const minimum = Math.max(1, Number(quantityInput?.min || 1));
      const maximum = Math.max(minimum, Number(quantityInput?.max || 6));
      return clamp(Number.parseInt(quantityInput?.value || '1', 10) || 1, minimum, maximum);
    };

    const showStatus = (message) => {
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
    };

    const planById = (planId) => plans.find((plan) => Number(plan.dataset.aofpCourseOption) === Number(planId));
    const planReady = (planId) => {
      if (!protocolAvailable || !baseBridgeValid) return false;
      if (Number(planId) === 3) return oilAvailable && systemBridgeValid;
      return Number(planId) === 1 || Number(planId) === 2;
    };

    const syncBridge = () => {
      if (!baseBridgeValid || !expectedPlans[currentPlan]) return false;
      const multiplier = getQuantity();
      const selectedRadio = nativeRadios[currentPlan - 1];
      nativeOffers.forEach((offer) => {
        // The visible Protocol controller owns this hidden multi-item bridge.
        // Keep Shrine's ProductForm aggregation, but prevent its gift element
        // from independently adding/removing Oil after later cart updates.
        offer.dataset.mainProduct = '';
        offer.dataset.saveLocalStorage = 'false';
        offer.mainProduct = '';
        offer.saveLocalStorage = false;
      });
      nativeRadios.forEach((radio, index) => {
        if (!radio) return;
        radio.value = radio.dataset.aofpBaseQuantity || String(expectedPlans[index + 1].protocol);
      });
      selectedRadio.value = String(expectedPlans[currentPlan].protocol * multiplier);
      selectedRadio.checked = true;
      nativeVariant.value = protocolVariantId;
      nativeBreaks.selectedQuantity = Number(selectedRadio.value);
      nativeBreaks.selectedOffer = currentPlan;

      nativeOffers.forEach((offer) => {
        offer.unlockedItems = [];
        offer.querySelectorAll('.quantity-break__upsell[data-product]').forEach((item) => {
          item.dataset.unlocked = 'false';
          item.classList.remove('quantity-gift--unlocked');
        });
      });
      const systemSelected = currentPlan === 3 && systemBridgeValid;
      if (systemOilItem) systemOilItem.dataset.selected = String(systemSelected);
      if (systemSelected) {
        systemOffer.unlockedItems = Array.from({ length: multiplier }, () => oilVariantId);
        systemOilItem.dataset.unlocked = 'true';
        systemOilItem.classList.add('quantity-gift--unlocked');
      }
      nativeBreaks.dataset.aofpCoursePlan = String(currentPlan);
      nativeBreaks.dataset.aofpCourseQuantity = String(multiplier);
      return true;
    };

    const purchaseButtons = () => [
      ...root.querySelectorAll('[data-aofp-course-submit], [data-aofp-course-buy-now]')
    ];

    const updatePurchaseState = () => {
      const ready = planReady(currentPlan) && !busy;
      root.toggleAttribute('aria-busy', busy);
      purchaseButtons().forEach((button) => {
        button.disabled = !ready;
        button.setAttribute('aria-disabled', String(!ready));
      });
      scope.querySelectorAll('[data-aofp-submit-proxy]').forEach((button) => {
        if ('disabled' in button) button.disabled = !ready;
        button.setAttribute('aria-disabled', String(!ready));
      });
      scope.querySelectorAll('[data-aofp-course-pick]').forEach((button) => {
        const targetPlan = Number(button.dataset.aofpCoursePick);
        const enabled = planReady(targetPlan) && !busy;
        if ('disabled' in button) button.disabled = !enabled;
        button.setAttribute('aria-disabled', String(!enabled));
      });
      [quantityMinus, quantityPlus].forEach((button) => {
        if (button) button.disabled = busy;
      });
      if (quantityInput) quantityInput.readOnly = busy;
      plans.forEach((plan) => {
        const planId = Number(plan.dataset.aofpCourseOption);
        const permanentlyUnavailable = !planReady(planId);
        plan.disabled = permanentlyUnavailable;
        plan.setAttribute('aria-disabled', String(permanentlyUnavailable || busy));
      });
    };

    const renderSelection = () => {
      const plan = planById(currentPlan);
      if (!plan) return;
      const multiplier = getQuantity();
      if (quantityInput) quantityInput.value = String(multiplier);
      const total = Number(plan.dataset.price || 0) * multiplier;
      const compareTotal = Number(plan.dataset.compare || 0) * multiplier;
      const hasCompare = compareTotal > total;
      const formattedPrice = formatMoney(total, moneyFormat);
      const formattedCompare = formatMoney(compareTotal, moneyFormat);
      const detail = String(plan.dataset.detail || '');
      const quantityDetail = multiplier > 1 ? multiplier + ' × ' + detail : detail;

      root.dataset.aofpCoursePlan = String(currentPlan);
      root.dataset.aofpCourseQuantity = String(multiplier);
      plans.forEach((candidate) => {
        const active = candidate === plan;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-checked', String(active));
        candidate.tabIndex = active ? 0 : -1;
      });
      root.querySelectorAll('[data-aofp-course-price], [data-aofp-submit-price], [data-aofp-sticky-price]').forEach((node) => {
        node.textContent = formattedPrice;
      });
      scope.querySelectorAll('[data-aofp-proxy-price]').forEach((node) => {
        node.textContent = formattedPrice;
      });
      root.querySelectorAll('[data-aofp-course-compare]').forEach((node) => {
        node.textContent = hasCompare ? formattedCompare : '';
        node.hidden = !hasCompare;
      });
      root.querySelectorAll('[data-aofp-course-detail], [data-aofp-sticky-detail], [data-aofp-quantity-note]').forEach((node) => {
        node.textContent = quantityDetail;
      });
      root.querySelectorAll('[data-aofp-sticky-name]').forEach((node) => {
        node.textContent = plan.dataset.name || '';
      });
      syncBridge();
      updatePurchaseState();
    };

    const selectPlan = (planId, options = {}) => {
      const { focus = false, clearStatus = true } = options;
      const numericPlan = Number(planId);
      const plan = planById(numericPlan);
      if (!plan || busy) return false;
      if (!planReady(numericPlan)) {
        if (numericPlan === 3 && !oilAvailable) showStatus(copy.oil);
        else if (!protocolAvailable) showStatus(copy.product);
        else showStatus(copy.config);
        return false;
      }
      currentPlan = numericPlan;
      renderSelection();
      if (clearStatus) showStatus('');
      if (focus) plan.focus({ preventScroll: true });
      return true;
    };

    plans.forEach((plan) => {
      plan.addEventListener('click', () => selectPlan(plan.dataset.aofpCourseOption), { signal });
      plan.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        const enabledPlans = plans.filter((candidate) => planReady(candidate.dataset.aofpCourseOption));
        if (!enabledPlans.length) return;
        event.preventDefault();
        const activePosition = Math.max(0, enabledPlans.indexOf(plan));
        let targetPosition = activePosition;
        if (event.key === 'Home') targetPosition = 0;
        else if (event.key === 'End') targetPosition = enabledPlans.length - 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          targetPosition = (activePosition - 1 + enabledPlans.length) % enabledPlans.length;
        } else {
          targetPosition = (activePosition + 1) % enabledPlans.length;
        }
        selectPlan(enabledPlans[targetPosition].dataset.aofpCourseOption, { focus: true });
      }, { signal });
    });

    const setQuantity = (nextQuantity) => {
      if (busy || !quantityInput) return;
      quantityInput.value = String(nextQuantity);
      renderSelection();
      showStatus('');
    };
    quantityMinus?.addEventListener('click', () => setQuantity(getQuantity() - 1), { signal });
    quantityPlus?.addEventListener('click', () => setQuantity(getQuantity() + 1), { signal });
    quantityInput?.addEventListener('change', () => setQuantity(getQuantity()), { signal });
    quantityInput?.addEventListener('input', () => {
      if (quantityInput.value === '') return;
      setQuantity(getQuantity());
    }, { signal });

    const selectedDeltaMatches = (before, after, selectedPlan, multiplier, verifyCount = true) => {
      if (!before || !after) return false;
      const expected = expectedPlans[selectedPlan];
      const expectedProtocol = expected.protocol * multiplier;
      const expectedOil = expected.oil * multiplier;
      return variantQuantity(after, protocolVariantId) - variantQuantity(before, protocolVariantId) === expectedProtocol
        && variantQuantity(after, oilVariantId) - variantQuantity(before, oilVariantId) === expectedOil
        && (!verifyCount || Number(after.item_count) - Number(before.item_count) === expectedProtocol + expectedOil);
    };

    const invokeNative = async (trigger, checkout = false) => {
      if (busy || !planReady(currentPlan)) {
        if (!planReady(currentPlan)) showStatus(!protocolAvailable ? copy.product : copy.config);
        return;
      }
      const submittedPlan = currentPlan;
      const submittedQuantity = getQuantity();
      busy = true;
      updatePurchaseState();
      showStatus(copy.adding);

      try {
        const before = await fetchCart();
        if (!before) throw new Error(copy.cartError);
        if (!syncBridge()) throw new Error(copy.config);

        const errorNode = nativeRoot.querySelector('.product-form__error-message, [data-error-message]');
        const previousError = errorNode?.textContent?.trim() || '';
        if (nativeSubmit.disabled && protocolAvailable) nativeSubmit.disabled = false;
        nativeSubmit.click();

        const deadline = performance.now() + 10000;
        let confirmedCart = null;
        while (performance.now() < deadline) {
          await wait(250);
          const currentError = errorNode?.textContent?.trim() || '';
          if (currentError && currentError !== previousError) throw new Error(currentError);
          const remaining = Math.max(300, deadline - performance.now());
          const after = await fetchCart(Math.min(1500, remaining));
          if (selectedDeltaMatches(before, after, submittedPlan, submittedQuantity)) {
            confirmedCart = after;
            break;
          }
        }

        if (!confirmedCart) {
          showStatus(copy.cartReview);
          openCart(trigger);
          return;
        }

        syncCartCount(confirmedCart.item_count);
        showStatus('');
        if (checkout) {
          // Theme Editor preview: checkout cannot load inside the preview frame, so show the cart instead.
          if (window.Shopify?.designMode) {
            openCart(trigger);
            return;
          }
          window.location.assign('/checkout');
          return;
        }

        const drawerDeadline = performance.now() + 1800;
        while (performance.now() < drawerDeadline) {
          const drawerCart = readDrawerCart();
          if (selectedDeltaMatches(before, drawerCart, submittedPlan, submittedQuantity, false)) break;
          await wait(120);
        }
        const drawer = document.querySelector('cart-drawer');
        if (!drawer?.classList.contains('active')) openCart(trigger);
      } catch (error) {
        showStatus(error?.message || copy.cartError);
      } finally {
        if (!checkout || !document.hidden) {
          busy = false;
          if (protocolAvailable) {
            nativeSubmit.disabled = false;
            nativeSubmit.removeAttribute('aria-disabled');
          }
          updatePurchaseState();
        }
      }
    };

    root.querySelectorAll('[data-aofp-course-submit]').forEach((button) => {
      button.addEventListener('click', () => invokeNative(button, false), { signal });
    });
    root.querySelectorAll('[data-aofp-course-buy-now]').forEach((button) => {
      button.addEventListener('click', () => invokeNative(button, true), { signal });
    });
    nativeForm?.addEventListener('submit', syncBridge, { capture: true, signal });
    window.addEventListener('pageshow', () => {
      busy = false;
      updatePurchaseState();
    }, { signal });

    root.aofpSelectCourse = selectPlan;
    root.aofpPurchase = (trigger, checkout = false) => invokeNative(trigger, checkout);
    root.aofpRefreshCommerce = renderSelection;
    signal.addEventListener('abort', () => {
      delete root.aofpSelectCourse;
      delete root.aofpPurchase;
      delete root.aofpRefreshCommerce;
    }, { once: true });

    if (!protocolAvailable) showStatus(copy.product);
    else if (!baseBridgeValid) showStatus(copy.config);
    else if (!planReady(currentPlan)) {
      currentPlan = plans.find((plan) => planReady(plan.dataset.aofpCourseOption))
        ? Number(plans.find((plan) => planReady(plan.dataset.aofpCourseOption)).dataset.aofpCourseOption)
        : 1;
    }
    renderSelection();
    customElements.whenDefined('quantity-break-upsells').then(() => {
      if (!signal.aborted) renderSelection();
    });
  };

  const connectScrollProgress = (root, track, scene, count, setIndex, signal) => {
    if (!track || !scene || count < 2 || !allowsMotion(root)) {
      root.classList.add('aofp-motion-static');
      return {
        scrollToIndex: () => false,
        refresh: () => {}
      };
    }

    root.style.setProperty('--aofp-story-steps', String(count));
    let frame = 0;
    let intersecting = true;
    let observer;
    // Manual / deep-link selection lock.
    // 'flight': the page travels to the selected step (smooth scroll, plus at most two re-aims when layout above
    //   moved during the travel); the selected index stays locked.
    // 'hold': the travel has settled; the index stays locked until the page moves more than half a step away
    //   from where it settled (measured relative to the track), so landing tolerances and small corrections on
    //   short phone tracks never re-map the selection to a neighbouring step. Wheel / touch / scroll keys during
    //   the flight hand control back to the page at once.
    let manualIndex = null;
    let manualPhase = 'idle';
    let manualAnchor = 0;
    let manualCorrections = 0;
    let settleTimer = 0;
    let settleProbe = Number.NaN;
    let flightStarted = 0;
    let flightMoved = false;
    const SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'];

    const metrics = () => {
      const trackRect = track.getBoundingClientRect();
      const sceneRect = scene.getBoundingClientRect();
      const trackTop = window.scrollY + trackRect.top;
      const computedTop = Number.parseFloat(window.getComputedStyle(scene).top);
      const stickyTop = Number.isFinite(computedTop) ? Math.max(0, computedTop) : 0;
      const pinnedHeight = Math.min(
        Math.max(1, sceneRect.height || scene.offsetHeight),
        Math.max(1, window.innerHeight - stickyTop)
      );
      const range = Math.max(0, track.offsetHeight - pinnedHeight);
      return { start: trackTop - stickyTop, range };
    };

    // centre of a step on the track
    const targetFor = (index, start, range) => Math.max(0, start + ((index + 0.5) / count) * range);

    const clearManual = () => {
      manualIndex = null;
      manualPhase = 'idle';
      manualCorrections = 0;
      window.clearTimeout(settleTimer);
      settleTimer = 0;
    };

    const scrollIndex = () => {
      if (!intersecting || signal.aborted) return;
      if (root.dataset.aofpEditorIndex != null) {
        setIndex(clamp(Number(root.dataset.aofpEditorIndex) || 0, 0, count - 1), { source: 'editor', announce: false });
        return;
      }
      const { start, range } = metrics();
      if (manualIndex !== null) {
        const halfStep = Math.max(12, range / count / 2);
        if (manualPhase !== 'hold' || Math.abs((window.scrollY - start) - manualAnchor) <= halfStep) {
          setIndex(manualIndex, { source: 'manual-lock', announce: false });
          return;
        }
        // the page really moved away from the settled selection: follow the scroll again
        clearManual();
      }
      if (range < 48) return;
      const progress = clamp((window.scrollY - start) / range, 0, 1);
      const index = Math.min(count - 1, Math.floor(progress * count));
      setIndex(index, { source: 'scroll', announce: false });
    };

    const schedule = () => {
      if (frame || signal.aborted) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        scrollIndex();
      });
    };

    const armSettle = (delay) => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, delay);
    };

    const beginFlight = (target) => {
      manualPhase = 'flight';
      flightStarted = performance.now();
      flightMoved = false;
      settleProbe = Number.NaN;
      armSettle(200);
      window.scrollTo({ top: target, behavior: 'smooth' });
    };

    function settle() {
      settleTimer = 0;
      if (manualIndex === null || manualPhase !== 'flight' || signal.aborted) return;
      const y = window.scrollY;
      const { start, range } = metrics();
      const target = targetFor(manualIndex, start, range);
      // the smooth scroll has not started yet (busy main thread): give it time before judging the landing
      if (!flightMoved && Math.abs(target - y) > 2 && performance.now() - flightStarted < 1200) {
        armSettle(150);
        return;
      }
      // settled means no movement between two probes
      if (!(Math.abs(y - settleProbe) <= 0.5)) {
        settleProbe = y;
        armSettle(120);
        return;
      }
      if (range >= 48) {
        const step = range / count;
        const nearTrack = y >= start - window.innerHeight && y <= start + range + window.innerHeight;
        if (nearTrack && manualCorrections < 2 && Math.abs(target - y) > Math.max(4, step * 0.2)) {
          // layout above moved during the travel (lazy media, reveals, scroll anchoring): re-aim at the step centre
          manualCorrections += 1;
          beginFlight(target);
          return;
        }
      }
      manualPhase = 'hold';
      manualAnchor = y - start;
      schedule();
    }

    const onScroll = () => {
      if (manualPhase === 'flight') {
        flightMoved = true;
        armSettle(180);
      }
      schedule();
    };

    // wheel / touch / scroll keys while the page is still travelling: the visitor takes over at once
    const onScrollIntent = (event) => {
      if (manualPhase !== 'flight') return;
      if (event.type === 'keydown') {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !SCROLL_KEYS.includes(event.key)) return;
        const target = event.target;
        if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName || '')) return;
      }
      clearManual();
      schedule();
    };

    // a press on the page scrollbar (drag or track click, reported on the root with a position outside the client
    // box) or a middle-button autoscroll during the travel is also a takeover; touch is handled by touchstart
    const onPointerIntent = (event) => {
      if (manualPhase !== 'flight' || event.pointerType === 'touch') return;
      const doc = document.documentElement;
      if (event.button === 1 || event.clientX >= doc.clientWidth || event.clientY >= doc.clientHeight) onScrollIntent(event);
    };

    const scrollToIndex = (requestedIndex) => {
      const index = clamp(Number(requestedIndex) || 0, 0, count - 1);
      const { start, range } = metrics();
      if (range < 48) return false;
      manualIndex = index;
      manualCorrections = 0;
      beginFlight(targetFor(index, start, range));
      return true;
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    window.addEventListener('orientationchange', schedule, { passive: true, signal });
    window.addEventListener('wheel', onScrollIntent, { passive: true, signal });
    window.addEventListener('touchstart', onScrollIntent, { passive: true, signal });
    window.addEventListener('keydown', onScrollIntent, { signal });
    window.addEventListener('pointerdown', onPointerIntent, { passive: true, signal });
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        intersecting = entries.some((entry) => entry.isIntersecting);
        if (intersecting) schedule();
      }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });
      observer.observe(track);
    }
    signal.addEventListener('abort', () => {
      observer?.disconnect();
      clearManual();
      if (frame) window.cancelAnimationFrame(frame);
    }, { once: true });
    schedule();
    return { scrollToIndex, refresh: schedule };
  };

  const markStateChange = (root, previous, next, timers) => {
    root.dataset.aofpStateDirection = next > previous ? 'forward' : next < previous ? 'backward' : 'still';
    root.classList.remove('is-state-changing');
    void root.offsetWidth;
    root.classList.add('is-state-changing');
    const previousTimer = timers.get(root);
    if (previousTimer) window.clearTimeout(previousTimer);
    const timer = window.setTimeout(() => {
      root.classList.remove('is-state-changing');
      timers.delete(root);
    }, 240);
    timers.set(root, timer);
  };

  const bindIndexedKeyboard = (buttons, select, signal) => {
    buttons.forEach((button) => {
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        const siblings = buttons.filter((candidate) => candidate.parentElement === button.parentElement);
        const position = siblings.indexOf(button);
        if (position < 0 || !siblings.length) return;
        event.preventDefault();
        let nextPosition = position;
        if (event.key === 'Home') nextPosition = 0;
        else if (event.key === 'End') nextPosition = siblings.length - 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextPosition = (position - 1 + siblings.length) % siblings.length;
        } else {
          nextPosition = (position + 1) % siblings.length;
        }
        const target = siblings[nextPosition];
        select(Number(target.dataset.aofpCourseSelect ?? target.dataset.aofpFormulaSelect), {
          source: 'keyboard',
          announce: true,
          scroll: true
        });
        target.focus({ preventScroll: true });
      }, { signal });
    });
  };

  const initCourseStory = (root, signal) => {
    if (!root.matches('[data-aofp-course]')) return;
    const selectors = [...root.querySelectorAll('[data-aofp-course-select]')];
    const panels = [...root.querySelectorAll('[data-aofp-course-panel]')];
    if (!selectors.length || !panels.length) return;

    const count = Math.min(Number(root.dataset.stepCount) || panels.length, panels.length);
    const track = root.querySelector('[data-aofp-scrub-track="course"]');
    const scene = root.querySelector('[data-aofp-sticky-scene="course"]');
    const phases = [...root.querySelectorAll('[data-aofp-course-phase]')];
    const previous = root.querySelector('[data-aofp-prev="course"]');
    const next = root.querySelector('[data-aofp-next="course"]');
    const currentNumber = root.querySelector('[data-aofp-course-number]');
    const doseLabel = root.querySelector('[data-aofp-course-dose-label]');
    const remaining = root.querySelector('[data-aofp-course-remaining]');
    const announcer = root.querySelector('[data-aofp-course-announcer]');
    const remainingLabel = (remaining?.textContent || 'still sealed').trim().replace(/^\d+\s*/, '') || 'still sealed';
    const transitionTimers = new Map();
    let activeIndex = clamp(Number(root.dataset.activeIndex) || 0, 0, count - 1);
    let scroller;

    const setIndex = (requested, options = {}) => {
      const { announce = false, scroll = false } = options;
      const index = clamp(Number(requested) || 0, 0, count - 1);
      const previousIndex = activeIndex;
      activeIndex = index;
      root.dataset.activeIndex = String(index);
      root.style.setProperty('--aofp-active-index', String(index));
      root.style.setProperty('--aofp-story-progress', String(count > 1 ? index / (count - 1) : 0));
      root.classList.toggle('is-course-complete', index === count - 1);
      root.classList.toggle('is-ampoule-open', index >= 2);

      selectors.forEach((button) => {
        const buttonIndex = Number(button.dataset.aofpCourseSelect);
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        button.classList.toggle('is-past', buttonIndex < index);
        button.classList.toggle('is-sealed', buttonIndex > index);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = Number(panel.dataset.aofpCoursePanel) === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      phases.forEach((phase) => {
        const start = Number(phase.dataset.aofpCourseTarget || 0);
        const end = Number(phase.dataset.aofpCoursePhaseEnd || start);
        const active = index >= start && index <= end;
        phase.classList.toggle('is-active', active);
        phase.setAttribute('aria-pressed', String(active));
      });
      const formatted = String(index + 1).padStart(2, '0');
      if (currentNumber) currentNumber.textContent = formatted;
      if (doseLabel) doseLabel.textContent = formatted;
      if (remaining) {
        remaining.textContent = index === count - 1
          ? (document.documentElement.lang.split('-')[0].toLowerCase() === 'ar' ? 'اكتمل عرض الجرعات' : 'COURSE COMPLETE')
          : String(count - index - 1) + ' ' + remainingLabel;
      }
      if (previous) {
        previous.disabled = index === 0;
        previous.setAttribute('aria-disabled', String(index === 0));
      }
      if (next) {
        next.disabled = index === count - 1;
        next.setAttribute('aria-disabled', String(index === count - 1));
      }
      if (announce && announcer) {
        const title = panels.find((panel) => Number(panel.dataset.aofpCoursePanel) === index)
          ?.querySelector('h3')?.textContent?.trim();
        announcer.textContent = (document.documentElement.lang.split('-')[0].toLowerCase() === 'ar' ? 'الجرعة ' + formatted + ' من ' + count : 'Dose ' + formatted + ' of ' + count) + (title ? ': ' + title : '');
      }
      if (previousIndex !== index) markStateChange(root, previousIndex, index, transitionTimers);
      if (scroll) scroller?.scrollToIndex(index);
      return true;
    };

    selectors.forEach((button) => {
      button.addEventListener('click', () => setIndex(button.dataset.aofpCourseSelect, {
        source: 'manual',
        announce: true,
        scroll: true
      }), { signal });
    });
    bindIndexedKeyboard(selectors, setIndex, signal);
    phases.forEach((phase) => {
      phase.addEventListener('click', () => setIndex(phase.dataset.aofpCourseTarget, {
        source: 'phase',
        announce: true,
        scroll: true
      }), { signal });
    });
    previous?.addEventListener('click', () => setIndex(activeIndex - 1, {
      source: 'previous',
      announce: true,
      scroll: true
    }), { signal });
    next?.addEventListener('click', () => setIndex(activeIndex + 1, {
      source: 'next',
      announce: true,
      scroll: true
    }), { signal });

    scroller = connectScrollProgress(root, track, scene, count, setIndex, signal);
    root.aofpStorySelect = setIndex;
    root.aofpStoryRefresh = scroller.refresh;
    root.aofpStoryScrollTo = scroller.scrollToIndex;
    signal.addEventListener('abort', () => {
      transitionTimers.forEach((timer) => window.clearTimeout(timer));
      delete root.aofpStorySelect;
      delete root.aofpStoryRefresh;
      delete root.aofpStoryScrollTo;
    }, { once: true });
    setIndex(activeIndex, { announce: false });
  };

  const initFormulaStory = (root, signal) => {
    if (!root.matches('[data-aofp-formula]')) return;
    const selectors = [...root.querySelectorAll('[data-aofp-formula-select]')];
    const orbs = [...root.querySelectorAll('[data-aofp-formula-orb]')];
    const panels = [...root.querySelectorAll('[data-aofp-formula-panel]')];
    if (!selectors.length || !orbs.length || !panels.length) return;

    const count = Math.min(Number(root.dataset.stepCount) || panels.length, panels.length);
    const track = root.querySelector('[data-aofp-scrub-track="formula"]');
    const scene = root.querySelector('[data-aofp-sticky-scene="formula"]');
    const field = root.querySelector('[data-aofp-formula-field]');
    const previous = root.querySelector('[data-aofp-prev="formula"]');
    const next = root.querySelector('[data-aofp-next="formula"]');
    const announcer = root.querySelector('[data-aofp-formula-announcer]');
    const transitionTimers = new Map();
    let activeIndex = clamp(Number(root.dataset.activeIndex) || 0, 0, count - 1);
    let scroller;

    const setIndex = (requested, options = {}) => {
      const { announce = false, scroll = false } = options;
      const index = clamp(Number(requested) || 0, 0, count - 1);
      const previousIndex = activeIndex;
      activeIndex = index;
      const activeOrb = orbs.find((orb) => Number(orb.dataset.aofpFormulaOrb) === index);
      root.dataset.activeIndex = String(index);
      root.dataset.aofpActiveMaterial = activeOrb?.dataset.aofpFormulaMaterial || '';
      root.dataset.aofpActiveKey = activeOrb?.dataset.aofpFormulaKey || '';
      root.style.setProperty('--aofp-active-index', String(index));
      root.style.setProperty('--aofp-story-progress', String(count > 1 ? index / (count - 1) : 0));
      if (field) {
        field.dataset.aofpActiveMaterial = activeOrb?.dataset.aofpFormulaMaterial || '';
        field.dataset.aofpActiveKey = activeOrb?.dataset.aofpFormulaKey || '';
      }

      selectors.forEach((button) => {
        const active = Number(button.dataset.aofpFormulaSelect) === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      orbs.forEach((orb) => {
        const orbIndex = Number(orb.dataset.aofpFormulaOrb);
        orb.classList.toggle('is-before', orbIndex < index);
        orb.classList.toggle('is-after', orbIndex > index);
      });
      panels.forEach((panel) => {
        const active = Number(panel.dataset.aofpFormulaPanel) === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      if (previous) {
        previous.disabled = index === 0;
        previous.setAttribute('aria-disabled', String(index === 0));
      }
      if (next) {
        next.disabled = index === count - 1;
        next.setAttribute('aria-disabled', String(index === count - 1));
      }
      if (announce && announcer) {
        const panel = panels.find((candidate) => Number(candidate.dataset.aofpFormulaPanel) === index);
        const name = panel?.querySelector('h3')?.textContent?.trim() || activeOrb?.textContent?.trim() || '';
        announcer.textContent = document.documentElement.lang.split('-')[0].toLowerCase() === 'ar' ? 'تفاصيل المكوّن: ' + name + '، ' + (index + 1) + ' من ' + count : 'Open active: ' + name + ', ' + (index + 1) + ' of ' + count;
      }
      if (previousIndex !== index) markStateChange(root, previousIndex, index, transitionTimers);
      if (scroll) scroller?.scrollToIndex(index);
      return true;
    };

    selectors.forEach((button) => {
      button.addEventListener('click', () => setIndex(button.dataset.aofpFormulaSelect, {
        source: 'manual',
        announce: true,
        scroll: true
      }), { signal });
    });
    bindIndexedKeyboard(selectors, setIndex, signal);
    previous?.addEventListener('click', () => setIndex(activeIndex - 1, {
      source: 'previous',
      announce: true,
      scroll: true
    }), { signal });
    next?.addEventListener('click', () => setIndex(activeIndex + 1, {
      source: 'next',
      announce: true,
      scroll: true
    }), { signal });

    scroller = connectScrollProgress(root, track, scene, count, setIndex, signal);
    root.aofpStorySelect = setIndex;
    root.aofpStoryRefresh = scroller.refresh;
    root.aofpStoryScrollTo = scroller.scrollToIndex;
    signal.addEventListener('abort', () => {
      transitionTimers.forEach((timer) => window.clearTimeout(timer));
      delete root.aofpStorySelect;
      delete root.aofpStoryRefresh;
      delete root.aofpStoryScrollTo;
    }, { once: true });
    setIndex(activeIndex, { announce: false });
  };

  const initGenericStepper = (root, signal) => {
    const configurations = [
      {
        name: 'seal',
        selector: '[data-aofp-seal-select]',
        panel: '[data-aofp-seal-panel]',
        visual: '[data-aofp-seal-visual]'
      },
      {
        name: 'application',
        selector: '[data-aofp-application-select]',
        panel: '[data-aofp-application-panel]',
        visual: '[data-aofp-application-visual]'
      },
      {
        name: 'steps',
        selector: '[data-aofp-step-select]',
        panel: '[data-aofp-step-panel]',
        visual: '[data-aofp-step-visual], [data-aofp-step-layer]'
      }
    ];
    const configuration = configurations.find((candidate) => root.querySelector(candidate.selector));
    if (!configuration) return;

    const selectors = [...root.querySelectorAll(configuration.selector)];
    const panels = [...root.querySelectorAll(configuration.panel)];
    const visuals = [...root.querySelectorAll(configuration.visual)];
    const previous = root.querySelector(
      '[data-aofp-prev="' + configuration.name + '"], [data-aofp-step-prev]'
    );
    const next = root.querySelector(
      '[data-aofp-next="' + configuration.name + '"], [data-aofp-step-next]'
    );
    const announcer = root.querySelector(
      '[data-aofp-step-announcer="' + configuration.name + '"], [data-aofp-step-announcer]'
    );
    const cyclicNext = Boolean(next?.matches('[data-aofp-step-next="application"]') && !previous);
    const readIndex = (node) => Number(
      node.dataset.aofpSealSelect
      ?? node.dataset.aofpApplicationSelect
      ?? node.dataset.aofpStepSelect
      ?? node.dataset.aofpSealPanel
      ?? node.dataset.aofpApplicationPanel
      ?? node.dataset.aofpStepPanel
      ?? node.dataset.aofpSealVisual
      ?? node.dataset.aofpApplicationVisual
      ?? node.dataset.aofpStepVisual
      ?? node.dataset.aofpStepLayer
      ?? 0
    );
    const count = Math.max(
      Number(root.dataset.stepCount) || 0,
      ...selectors.map(readIndex),
      ...panels.map(readIndex),
      ...visuals.map(readIndex)
    ) + (root.dataset.stepCount ? 0 : 1);
    let activeIndex = clamp(Number(root.dataset.activeIndex) || 0, 0, Math.max(0, count - 1));

    const setIndex = (requested, options = {}) => {
      const index = clamp(Number(requested) || 0, 0, Math.max(0, count - 1));
      activeIndex = index;
      root.dataset.activeIndex = String(index);
      root.style.setProperty('--aofp-active-index', String(index));
      root.style.setProperty('--aofp-story-progress', String(count > 1 ? index / (count - 1) : 0));
      selectors.forEach((button) => {
        const active = readIndex(button) === index;
        button.classList.toggle('is-active', active);
        button.setAttribute(button.getAttribute('role') === 'tab' ? 'aria-selected' : 'aria-pressed', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = readIndex(panel) === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      visuals.forEach((visual) => {
        const active = readIndex(visual) === index;
        visual.classList.toggle('is-active', active);
        visual.setAttribute('aria-hidden', String(!active));
      });
      if (previous) {
        previous.disabled = index === 0;
        previous.setAttribute('aria-disabled', String(index === 0));
      }
      if (next) {
        next.disabled = !cyclicNext && index === count - 1;
        next.setAttribute('aria-disabled', String(!cyclicNext && index === count - 1));
      }
      if (options.announce && announcer) {
        const panel = panels.find((candidate) => readIndex(candidate) === index);
        announcer.textContent = (document.documentElement.lang.split('-')[0].toLowerCase() === 'ar' ? 'الخطوة ' + (index + 1) + ' من ' + count : 'Step ' + (index + 1) + ' of ' + count) + ': ' + (panel?.textContent?.trim() || '');
      }
      if (options.focus) selectors.find((button) => readIndex(button) === index)?.focus({ preventScroll: true });
      return true;
    };

    selectors.forEach((button) => {
      button.addEventListener('click', () => setIndex(readIndex(button), { announce: true }), { signal });
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let target = activeIndex;
        if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = count - 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (activeIndex - 1 + count) % count;
        else target = (activeIndex + 1) % count;
        setIndex(target, { focus: true, announce: true });
      }, { signal });
    });
    previous?.addEventListener('click', () => setIndex(activeIndex - 1, { announce: true }), { signal });
    next?.addEventListener('click', () => {
      const target = cyclicNext && activeIndex === count - 1 ? 0 : activeIndex + 1;
      setIndex(target, { announce: true });
    }, { signal });
    root.aofpStepSelect = setIndex;
    signal.addEventListener('abort', () => { delete root.aofpStepSelect; }, { once: true });
    setIndex(activeIndex);
  };

  const initSeal = (root, signal) => {
    if (!root.matches('[data-aofp-seal]')) return;
    const toggles = [...root.querySelectorAll('[data-aofp-seal-toggle]')];
    if (!toggles.length) return;
    const layers = [...root.querySelectorAll('[data-aofp-seal-layer]')];
    const label = root.querySelector('[data-aofp-seal-label]');
    const prompt = root.querySelector('[data-aofp-seal-prompt]');
    const announcer = root.querySelector('[data-aofp-seal-announcer]');
    let snapped = root.dataset.sealState === 'snapped';

    const render = (announce = false) => {
      const state = snapped ? 'snapped' : 'sealed';
      const stateLabel = snapped ? root.dataset.snappedLabel : root.dataset.sealedLabel;
      const actionPrompt = snapped ? root.dataset.snappedPrompt : root.dataset.sealedPrompt;
      root.dataset.sealState = state;
      root.classList.toggle('is-snapped', snapped);
      root.classList.toggle('is-sealed', !snapped);
      toggles.forEach((toggle) => {
        toggle.setAttribute('aria-pressed', String(snapped));
        toggle.setAttribute('aria-label', actionPrompt || stateLabel || '');
      });
      layers.forEach((layer) => {
        const active = layer.dataset.aofpSealLayer === (snapped ? 'snap' : 'sealed');
        layer.classList.toggle('is-active', active);
        layer.setAttribute('aria-hidden', String(!active));
      });
      if (label) label.textContent = stateLabel || '';
      if (prompt) prompt.textContent = actionPrompt || '';
      if (announce && announcer) announcer.textContent = stateLabel || state;
    };

    toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        snapped = !snapped;
        render(true);
      }, { signal });
    });
    root.aofpSetSealState = (state, announce = false) => {
      snapped = state === 'snapped' || state === true;
      render(announce);
    };
    signal.addEventListener('abort', () => { delete root.aofpSetSealState; }, { once: true });
    render(false);
  };

  const initFaq = (root, signal) => {
    const buttons = [...root.querySelectorAll('[data-aofp-faq-button]')];
    if (!buttons.length) return;

    const apply = (button, expanded) => {
      const answerId = button.getAttribute('aria-controls');
      const answer = answerId ? document.getElementById(answerId) : null;
      const item = button.closest('.aofp-faq__item');
      const icon = button.querySelector('.aofp-faq__icon');
      button.setAttribute('aria-expanded', String(expanded));
      if (answer) answer.hidden = !expanded;
      item?.classList.toggle('is-open', expanded);
      if (icon) icon.textContent = expanded ? '−' : '+';
    };
    const setExpanded = (button, expanded, closeOthers = true) => {
      if (!button || !buttons.includes(button)) return;
      if (expanded && closeOthers) buttons.forEach((candidate) => {
        if (candidate !== button) apply(candidate, false);
      });
      apply(button, expanded);
    };
    buttons.forEach((button) => {
      apply(button, button.getAttribute('aria-expanded') === 'true');
      button.addEventListener('click', () => {
        setExpanded(button, button.getAttribute('aria-expanded') !== 'true');
      }, { signal });
    });
    root.aofpSetFaqExpanded = setExpanded;
    signal.addEventListener('abort', () => { delete root.aofpSetFaqExpanded; }, { once: true });
  };

  const initReveals = (root, signal) => {
    const items = [...root.querySelectorAll('[data-aofp-reveal]')];
    if (!items.length) return;
    if (editorPreview() || !allowsMotion(root) || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.classList.remove('aofp-is-pending');
        item.classList.add('aofp__reveal', 'aofp-is-visible');
      });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('aofp-is-pending');
        entry.target.classList.add('aofp-is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.04 });
    items.forEach((item) => {
      item.classList.add('aofp__reveal', 'aofp-is-pending');
      item.style.transitionDelay = String(Number(item.dataset.aofpReveal || 0)) + 's';
      observer.observe(item);
    });
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });
  };

  const findBuyRoot = (origin) => {
    const scope = origin?.closest?.('main') || document;
    return scope.querySelector('[data-aofp-buy]');
  };

  const initExternalControls = (root, signal) => {
    root.querySelectorAll('[data-aofp-course-pick]').forEach((control) => {
      control.addEventListener('click', (event) => {
        const buyRoot = findBuyRoot(control);
        const selected = buyRoot?.aofpSelectCourse?.(Number(control.dataset.aofpCoursePick));
        const href = control.getAttribute('href') || '';
        const shouldScroll = href === '#buy' || href.endsWith('#buy') || control.dataset.aofpCourseScroll === 'true';
        if (shouldScroll) {
          event.preventDefault();
          if (selected !== false) {
            buyRoot?.scrollIntoView({
              block: 'start',
              behavior: allowsMotion(buyRoot || root) ? 'smooth' : 'auto'
            });
          }
        }
      }, { signal });
    });
    root.querySelectorAll('[data-aofp-submit-proxy]').forEach((control) => {
      control.addEventListener('click', () => {
        const buyRoot = findBuyRoot(control);
        buyRoot?.aofpPurchase?.(control, false);
      }, { signal });
    });
    root.querySelectorAll('[data-aofp-formula-jump]').forEach((control) => {
      control.addEventListener('click', () => {
        const formulaRoot = pageScope(root).querySelector('[data-aofp-formula]');
        if (!formulaRoot) return;
        const index = Number(control.dataset.aofpFormulaJump);
        formulaRoot.aofpStorySelect?.(index, {
          source: 'teaser',
          announce: true,
          scroll: false
        });
        const positioned = formulaRoot.aofpStoryScrollTo?.(index);
        if (!positioned) {
          formulaRoot.scrollIntoView({ block: 'start', behavior: allowsMotion(formulaRoot) ? 'smooth' : 'auto' });
        }
      }, { signal });
    });
    root.querySelectorAll('[data-aofp-formula-explore]').forEach((control) => {
      control.addEventListener('click', (event) => {
        const formulaRoot = pageScope(root).querySelector('[data-aofp-formula]');
        if (!formulaRoot) return;
        event.preventDefault();
        formulaRoot.aofpStorySelect?.(0, {
          source: 'teaser-link',
          announce: true,
          scroll: false
        });
        const positioned = formulaRoot.aofpStoryScrollTo?.(0);
        if (!positioned) {
          formulaRoot.scrollIntoView({ block: 'start', behavior: allowsMotion(formulaRoot) ? 'smooth' : 'auto' });
        }
      }, { signal });
    });
  };

  const initSticky = (root, signal) => {
    if (!root.matches('[data-aofp-buy]')) return;
    const sticky = root.querySelector('[data-aofp-sticky]');
    if (!sticky) return;
    const scope = pageScope(root);
    let buyVisible = true;
    let closingVisible = false;
    let buyObserver;
    let closingObserver;
    let scrollFrame = 0;

    const update = () => {
      const visible = !buyVisible && !closingVisible;
      sticky.classList.toggle('is-visible', visible);
      sticky.toggleAttribute('inert', !visible);
      sticky.setAttribute('aria-hidden', String(!visible));
    };

    const refreshClosing = (excludedRoot) => {
      closingObserver?.disconnect();
      closingObserver = undefined;
      closingVisible = false;
      const closing = [...scope.querySelectorAll('[data-aofp-closing]')].find((node) => {
        if (!excludedRoot) return true;
        return node !== excludedRoot && !excludedRoot.contains?.(node);
      });
      if (closing && 'IntersectionObserver' in window) {
        closingObserver = new IntersectionObserver((entries) => {
          closingVisible = entries[0]?.isIntersecting ?? false;
          update();
        }, { threshold: 0.06 });
        closingObserver.observe(closing);
      }
      update();
    };

    if ('IntersectionObserver' in window) {
      buyObserver = new IntersectionObserver((entries) => {
        buyVisible = entries[0]?.isIntersecting ?? true;
        update();
      }, { rootMargin: '-96px 0px 0px 0px', threshold: 0 });
      buyObserver.observe(root);
    } else {
      const inspect = () => {
        scrollFrame = 0;
        const buyRect = root.getBoundingClientRect();
        const closing = scope.querySelector('[data-aofp-closing]');
        const closingRect = closing?.getBoundingClientRect();
        buyVisible = buyRect.bottom > 96 && buyRect.top < window.innerHeight;
        closingVisible = Boolean(closingRect && closingRect.top < window.innerHeight && closingRect.bottom > 0);
        update();
      };
      window.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(inspect);
      }, { passive: true, signal });
      inspect();
    }

    const stickyState = { refreshClosing };
    stickyStates.set(root, stickyState);
    signal.addEventListener('abort', () => {
      buyObserver?.disconnect();
      closingObserver?.disconnect();
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      sticky.classList.remove('is-visible');
      sticky.setAttribute('inert', '');
      sticky.setAttribute('aria-hidden', 'true');
      if (stickyStates.get(root) === stickyState) stickyStates.delete(root);
    }, { once: true });
    refreshClosing();
  };

  const initSection = (root) => {
    if (sectionControllers.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    sectionControllers.set(root, controller);
    root.dataset.aofpReady = 'true';
    root.classList.toggle('aofp-motion-static', !allowsMotion(root));
    signal.addEventListener('abort', () => {
      delete root.dataset.aofpReady;
      sectionControllers.delete(root);
    }, { once: true });

    initGallery(root, signal);
    initCommerce(root, signal);
    initCourseStory(root, signal);
    initFormulaStory(root, signal);
    initGenericStepper(root, signal);
    initSeal(root, signal);
    initFaq(root, signal);
    initReveals(root, signal);
    initExternalControls(root, signal);
    initSticky(root, signal);
  };

  // Only the buy section is on the first screen. Wiring all thirteen at DOMContentLoaded built thirteen
  // AbortControllers and their whole listener graph — twelve dose tabs, six formula actives, the proof
  // items, the FAQ buttons, the seal toggles, the steppers — for sections up to 16,000 px down, and several
  // of those initialisers measure their section, which would force the browser to lay out content that
  // content-visibility had skipped. The buy section stays eager so the gallery, price, purchase selector
  // and sticky bar are live immediately; every other section is built when it comes within two viewports.
  // Nothing server-rendered moves: all the copy, headings and links are in the HTML either way.
  let sectionWatcher = null;
  const watchSection = (root) => {
    if (sectionControllers.has(root)) return;
    if (root.matches('[data-aofp-buy]') || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      initSection(root);
      return;
    }
    if (!sectionWatcher) {
      sectionWatcher = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          sectionWatcher.unobserve(entry.target);
          initSection(entry.target);
        });
      }, { rootMargin: '200% 0px 200% 0px' });
    }
    sectionWatcher.observe(root);
  };

  const initAll = (scope = document) => rootsWithin(scope).forEach(watchSection);

  const destroyAll = (scope) => {
    rootsWithin(scope).forEach((root) => {
      sectionControllers.get(root)?.abort();
      sectionControllers.delete(root);
      delete root.dataset.aofpReady;
    });
  };

  const refreshStickies = (scope, excludedRoot) => {
    const page = scope?.closest?.('main') || document;
    const buyRoots = [];
    if (scope?.matches?.('[data-aofp-buy]')) buyRoots.push(scope);
    page.querySelectorAll?.('[data-aofp-buy]').forEach((root) => {
      if (!buyRoots.includes(root)) buyRoots.push(root);
    });
    buyRoots.forEach((root) => stickyStates.get(root)?.refreshClosing(excludedRoot));
  };

  const findEditorBlock = (blockId, eventTarget) => {
    if (!blockId) return null;
    if (eventTarget?.dataset?.aofpBlockId === blockId) return eventTarget;
    const blockScope = eventTarget?.closest?.('.shopify-section') || document;
    const direct = [...blockScope.querySelectorAll('[data-aofp-block-id]')]
      .find((node) => node.dataset.aofpBlockId === blockId)
      || [...document.querySelectorAll('[data-aofp-block-id]')].find((node) => node.dataset.aofpBlockId === blockId);
    if (direct) return direct;
    const candidates = [];
    if (eventTarget?.hasAttribute?.('data-shopify-editor-block')) candidates.push(eventTarget);
    eventTarget?.querySelectorAll?.('[data-shopify-editor-block]').forEach((node) => candidates.push(node));
    document.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
      if (!candidates.includes(node)) candidates.push(node);
    });
    return candidates.find((node) => String(node.getAttribute('data-shopify-editor-block') || '').includes(blockId)) || null;
  };

  const handleBlockSelect = (event) => {
    initAll(event.target);
    const blockId = String(event.detail?.blockId || '');
    const block = findEditorBlock(blockId, event.target);
    if (!block) return;
    const sectionRoot = block.closest(SECTION_SELECTOR);
    block.classList.add('is-editor-selected');
    let storyPositioned = false;

    if (block.matches('[data-aofp-course-option]')) {
      const buyRoot = findBuyRoot(block);
      if (buyRoot && buyRoot.dataset.aofpEditorPlan == null) {
        buyRoot.dataset.aofpEditorPlan = buyRoot.querySelector('[data-aofp-course-option][aria-checked="true"]')?.dataset.aofpCourseOption || '1';
      }
      buyRoot?.aofpSelectCourse?.(Number(block.dataset.aofpCourseOption));
    } else if (block.matches('[data-aofp-course-select], [data-aofp-formula-select]')) {
      const storyIndex = Number(block.matches('[data-aofp-course-select]') ? block.dataset.aofpCourseSelect : block.dataset.aofpFormulaSelect);
      if (sectionRoot) sectionRoot.dataset.aofpEditorIndex = String(storyIndex);
      sectionRoot?.aofpStorySelect?.(storyIndex, {
        source: 'editor',
        announce: false,
        scroll: false
      });
      storyPositioned = Boolean(sectionRoot?.aofpStoryScrollTo?.(storyIndex));
    } else if (block.matches('[data-aofp-gallery-slide]')) {
      const galleryHost = block.closest('[data-aofp-gallery]')?.aofpGallerySelectMedia
        ? block.closest('[data-aofp-gallery]')
        : block.closest('[data-aofp-buy]');
      galleryHost?.aofpGallerySelectMedia?.(block.dataset.aofpGalleryMedia);
    } else if (block.matches('[data-aofp-formula-jump]')) {
      pageScope(block).querySelector('[data-aofp-formula]')?.aofpStorySelect?.(
        Number(block.dataset.aofpFormulaJump),
        { source: 'editor', announce: false, scroll: false }
      );
    } else if (block.matches('[data-aofp-step-select]')) {
      if (sectionRoot && sectionRoot.dataset.aofpEditorStep == null) sectionRoot.dataset.aofpEditorStep = sectionRoot.dataset.activeIndex || '0';
      sectionRoot?.aofpStepSelect?.(Number(block.dataset.aofpStepSelect), {
        focus: false,
        announce: false
      });
    }

    const faqButton = block.matches('[data-aofp-faq-button]')
      ? block
      : block.querySelector('[data-aofp-faq-button]');
    if (faqButton) {
      if (block.dataset.aofpEditorWasExpanded == null) {
        block.dataset.aofpEditorWasExpanded = String(faqButton.getAttribute('aria-expanded') === 'true');
      }
      sectionRoot?.aofpSetFaqExpanded?.(faqButton, true);
    }
    if (!storyPositioned) block.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  const handleBlockDeselect = (event) => {
    const blockId = String(event.detail?.blockId || '');
    const block = findEditorBlock(blockId, event.target);
    if (!block) return;
    const faqButton = block.matches('[data-aofp-faq-button]')
      ? block
      : block.querySelector('[data-aofp-faq-button]');
    if (faqButton && block.dataset.aofpEditorWasExpanded != null) {
      block.closest(SECTION_SELECTOR)?.aofpSetFaqExpanded?.(
        faqButton,
        block.dataset.aofpEditorWasExpanded === 'true',
        false
      );
      delete block.dataset.aofpEditorWasExpanded;
    }
    const storyRoot = block.closest(SECTION_SELECTOR);
    if (storyRoot && storyRoot.dataset.aofpEditorIndex != null) {
      delete storyRoot.dataset.aofpEditorIndex;
      storyRoot.aofpStoryRefresh?.();
    }
    if (block.matches('[data-aofp-course-option]')) {
      const buyRoot = findBuyRoot(block);
      if (buyRoot?.dataset.aofpEditorPlan != null) {
        buyRoot.aofpSelectCourse?.(Number(buyRoot.dataset.aofpEditorPlan));
        delete buyRoot.dataset.aofpEditorPlan;
      }
    }
    if (block.matches('[data-aofp-step-select]') && storyRoot?.dataset.aofpEditorStep != null) {
      storyRoot.aofpStepSelect?.(Number(storyRoot.dataset.aofpEditorStep), { focus: false, announce: false });
      delete storyRoot.dataset.aofpEditorStep;
    }
    if (block.matches('[data-aofp-gallery-slide]')) {
      const galleryHost = block.closest('[data-aofp-gallery]')?.aofpGalleryRestore
        ? block.closest('[data-aofp-gallery]')
        : block.closest('[data-aofp-buy]');
      galleryHost?.aofpGalleryRestore?.();
    }
    block.classList.remove('is-editor-selected');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
    refreshStickies(event.target);
    findBuyRoot(event.target)?.aofpRefreshCommerce?.();
  });
  document.addEventListener('shopify:section:select', (event) => {
    initAll(event.target);
    refreshStickies(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    destroyAll(event.target);
    refreshStickies(event.target, event.target);
  });
  document.addEventListener('shopify:block:select', handleBlockSelect);
  document.addEventListener('shopify:block:deselect', handleBlockDeselect);
})();
